import React, { useState, useEffect } from 'react';
import { X, Loader2, CheckCircle2, XCircle, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { dashboardApi } from '../../services/api';
import type { ValidationResultData, ValidationResultExpectation } from '../../types';

/* ───────── Helpers ───────── */

function scoreColor(score: number): string {
  if (score >= 90) return 'text-green-400';
  if (score >= 75) return 'text-blue-400';
  if (score >= 60) return 'text-yellow-400';
  return 'text-red-400';
}

function scoreBg(score: number): string {
  if (score >= 90) return 'bg-green-500/20 border-green-500/30';
  if (score >= 75) return 'bg-blue-500/20 border-blue-500/30';
  if (score >= 60) return 'bg-yellow-500/20 border-yellow-500/30';
  return 'bg-red-500/20 border-red-500/30';
}

/** Group results by column name */
function groupByColumn(results: ValidationResultExpectation[]) {
  const map = new Map<string, ValidationResultExpectation[]>();
  for (const r of results) {
    const col = r.expectation_config?.kwargs?.column
      || r.expectation_config?.kwargs?.column_list?.join(', ')
      || '_table_';
    if (!map.has(col)) map.set(col, []);
    map.get(col)!.push(r);
  }
  return map;
}

/* ───────── Props ───────── */

interface Props {
  resultId: number;
  projectName: string;
  groupName: string;
  onClose: () => void;
}

/* ───────── Component ───────── */

const ValidationReportModal: React.FC<Props> = ({ resultId, projectName, groupName, onClose }) => {
  const [data, setData] = useState<ValidationResultData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedColumns, setExpandedColumns] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'validation' | 'profiling'>('validation');

  useEffect(() => {
    const fetchResult = async () => {
      setLoading(true);
      setError('');
      try {
        const resp = await dashboardApi.validationResult(resultId);
        setData(resp.data.payload);
        // Auto-expand all columns
        if (resp.data.payload?.result?.results) {
          const cols = new Set<string>();
          for (const r of resp.data.payload.result.results) {
            const col = r.expectation_config?.kwargs?.column
              || r.expectation_config?.kwargs?.column_list?.join(', ')
              || '_table_';
            cols.add(col);
          }
          setExpandedColumns(cols);
        }
      } catch {
        setError('Failed to load validation result.');
      } finally {
        setLoading(false);
      }
    };
    fetchResult();
  }, [resultId]);

  const toggleColumn = (col: string) => {
    setExpandedColumns(prev => {
      const next = new Set(prev);
      if (next.has(col)) next.delete(col); else next.add(col);
      return next;
    });
  };

  const result = data?.result;
  const stats = result?.statistics;
  const columnGroups = result?.results ? groupByColumn(result.results) : new Map();
  const profilingMetrics = result?.meta?.profiling_metrics;
  const hasProfiling = profilingMetrics && !profilingMetrics.error;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-cargo-surface border border-cargo-border rounded-xl w-full max-w-5xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-cargo-border flex-shrink-0">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-cargo-text">Validation Report</h2>
            <p className="text-xs text-cargo-muted mt-1">
              {projectName} &middot; {groupName}
              {data?.data_asset && <> &middot; {data.data_asset}</>}
            </p>
          </div>
          <button onClick={onClose} className="ml-4 text-cargo-muted hover:text-cargo-text flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {loading ? (
            <div className="flex flex-col items-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-cargo-accent" />
              <p className="text-cargo-muted text-sm mt-2">Loading report...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center py-16">
              <AlertTriangle className="w-6 h-6 text-red-400" />
              <p className="text-red-400 text-sm mt-2">{error}</p>
            </div>
          ) : result ? (
            <>
              {/* Summary Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Total Rules', value: stats?.evaluated_expectations || 0, color: 'text-cargo-text' },
                  { label: 'Passed', value: stats?.successful_expectations || 0, color: 'text-green-400' },
                  { label: 'Failed', value: stats?.unsuccessful_expectations || 0, color: 'text-red-400' },
                  { label: 'DQ Score', value: `${(stats?.success_percent || 0).toFixed(1)}%`, color: scoreColor(stats?.success_percent || 0) },
                ].map(({ label, value, color }) => (
                  <div key={label} className={`rounded-lg border p-3 text-center ${scoreBg(label === 'DQ Score' ? (stats?.success_percent || 0) : 100)}`}>
                    <p className={`text-xl font-bold ${color}`}>{value}</p>
                    <p className="text-xs text-cargo-muted mt-0.5">{label}</p>
                  </div>
                ))}
              </div>

              {/* Overall status */}
              <div className="flex items-center gap-2">
                {result.success ? (
                  <><CheckCircle2 className="w-5 h-5 text-green-400" /><span className="text-green-400 font-medium text-sm">All expectations passed</span></>
                ) : (
                  <><XCircle className="w-5 h-5 text-red-400" /><span className="text-red-400 font-medium text-sm">Some expectations failed</span></>
                )}
              </div>

              {/* Tabs if profiling exists */}
              {hasProfiling && (
                <div className="flex gap-1 bg-cargo-panel rounded-lg p-1 w-fit">
                  {(['validation', 'profiling'] as const).map((tab) => (
                    <button key={tab} onClick={() => setActiveTab(tab)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        activeTab === tab ? 'bg-cargo-accent text-white' : 'text-cargo-muted hover:text-cargo-text'
                      }`}>
                      {tab === 'validation' ? 'Validation Results' : 'Profiling Metrics'}
                    </button>
                  ))}
                </div>
              )}

              {/* Validation Results Tab */}
              {activeTab === 'validation' && (
                <div className="space-y-3">
                  {Array.from(columnGroups.entries()).map(([column, rules]) => {
                    const isExpanded = expandedColumns.has(column);
                    const passCount = rules.filter((r: ValidationResultExpectation) => r.success).length;
                    const failCount = rules.length - passCount;
                    return (
                      <div key={column} className="border border-cargo-border rounded-lg overflow-hidden">
                        {/* Column header */}
                        <button onClick={() => toggleColumn(column)}
                          className="w-full flex items-center justify-between px-4 py-2.5 bg-cargo-panel/50 hover:bg-cargo-panel transition-colors">
                          <div className="flex items-center gap-2">
                            {isExpanded ? <ChevronDown className="w-4 h-4 text-cargo-muted" /> : <ChevronRight className="w-4 h-4 text-cargo-muted" />}
                            <span className="text-sm font-medium text-cargo-text font-mono">{column === '_table_' ? 'Table-level' : column}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {passCount > 0 && <span className="text-xs text-green-400">{passCount} passed</span>}
                            {failCount > 0 && <span className="text-xs text-red-400">{failCount} failed</span>}
                          </div>
                        </button>

                        {/* Rules */}
                        {isExpanded && (
                          <div className="divide-y divide-cargo-border/50">
                            {rules.map((r: ValidationResultExpectation, i: number) => (
                              <RuleCard key={i} rule={r} />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Profiling Tab */}
              {activeTab === 'profiling' && hasProfiling && (
                <ProfilingView metrics={profilingMetrics} />
              )}
            </>
          ) : null}
        </div>

        {/* Footer */}
        {data && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-cargo-border flex-shrink-0">
            <div className="flex items-center gap-4 text-xs text-cargo-muted">
              {data.created_date && <span>Run: <span className="text-cargo-text">{new Date(data.created_date).toLocaleString()}</span></span>}
              {data.checkpoint && <span>Checkpoint: <span className="text-cargo-text">{data.checkpoint}</span></span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ───────── Rule Card ───────── */

function RuleCard({ rule }: { rule: ValidationResultExpectation }) {
  const [showDetails, setShowDetails] = useState(!rule.success);
  const ec = rule.expectation_config;
  const res = rule.result;

  return (
    <div className={`px-4 py-2.5 border-l-4 ${rule.success ? 'border-l-green-500' : 'border-l-red-500'}`}>
      <div className="flex items-center gap-2 flex-wrap">
        {rule.success ? (
          <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold rounded bg-green-500/20 text-green-400">PASS</span>
        ) : (
          <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-500/20 text-red-400">FAIL</span>
        )}
        <code className="text-xs text-cargo-text">{ec.type}</code>
        {ec.kwargs?.mostly != null && (
          <span className="text-xs text-cargo-muted">mostly {ec.kwargs.mostly}</span>
        )}
        {!rule.success && (
          <button onClick={() => setShowDetails(!showDetails)}
            className="ml-auto text-xs text-cargo-muted hover:text-cargo-text">
            {showDetails ? 'hide' : 'details'}
          </button>
        )}
      </div>

      {showDetails && !rule.success && (
        <div className="mt-1.5 space-y-1">
          {res?.unexpected_count != null && (
            <p className="text-xs text-red-400">
              Unexpected: {res.unexpected_count}
              {res.unexpected_percent != null && ` (${res.unexpected_percent.toFixed(2)}%)`}
            </p>
          )}
          {res?.observed_value != null && (
            <p className="text-xs text-cargo-muted">Observed: {String(res.observed_value)}</p>
          )}
          {res?.partial_unexpected_list && res.partial_unexpected_list.length > 0 && (
            <p className="text-xs text-cargo-muted">
              Samples: {res.partial_unexpected_list.slice(0, 5).map((v, i) => (
                <code key={i} className="text-cargo-text bg-cargo-panel px-1 py-0.5 rounded mx-0.5">{String(v)}</code>
              ))}
            </p>
          )}
          {rule.exception_info?.raised_exception && (
            <p className="text-xs text-red-400">Exception: {rule.exception_info.exception_message}</p>
          )}
        </div>
      )}
    </div>
  );
}

/* ───────── Profiling View ───────── */

function ProfilingView({ metrics }: { metrics: any }) {
  const columns = metrics?.columns || [];
  const ruleResults = metrics?.rule_results || [];

  return (
    <div className="space-y-5">
      {/* Header stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Data Asset', value: metrics?.data_asset || '—' },
          { label: 'Rows', value: metrics?.row_count?.toLocaleString() || '—' },
          { label: 'Columns', value: metrics?.column_count || columns.length || '—' },
          { label: 'Source', value: metrics?.source_type || 'pandas' },
        ].map(({ label, value }) => (
          <div key={label} className="rounded-lg border border-cargo-border bg-cargo-panel/50 p-3 text-center">
            <p className="text-sm font-semibold text-cargo-text">{value}</p>
            <p className="text-xs text-cargo-muted mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Column metrics table */}
      {columns.length > 0 && (
        <div className="border border-cargo-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-cargo-panel">
                  <th className="px-3 py-2 text-left text-xs font-medium text-cargo-muted">Column</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-cargo-muted">Type</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-cargo-muted">Min</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-cargo-muted">Max</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-cargo-muted">Mean</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-cargo-muted">Median</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-cargo-muted">Unique</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-cargo-muted">Null %</th>
                </tr>
              </thead>
              <tbody>
                {columns.map((col: any) => {
                  const isNumeric = col.type !== 'VARCHAR' && col.type !== 'BOOLEAN';
                  const nullPct = col.null_percent ?? 0;
                  const nullBarColor = nullPct < 5 ? 'bg-green-500' : nullPct < 25 ? 'bg-yellow-500' : 'bg-red-500';
                  return (
                    <tr key={col.column_name} className="border-t border-cargo-border/50 hover:bg-cargo-panel/30">
                      <td className="px-3 py-2 font-mono text-cargo-text text-xs">{col.column_name}</td>
                      <td className="px-3 py-2">
                        <span className="px-1.5 py-0.5 text-[10px] rounded bg-cargo-panel text-cargo-muted">{col.type}</span>
                      </td>
                      <td className="px-3 py-2 text-right text-cargo-muted text-xs">{isNumeric && col.min != null ? col.min : '—'}</td>
                      <td className="px-3 py-2 text-right text-cargo-muted text-xs">{isNumeric && col.max != null ? col.max : '—'}</td>
                      <td className="px-3 py-2 text-right text-cargo-muted text-xs">{isNumeric && col.mean != null ? Number(col.mean).toFixed(2) : '—'}</td>
                      <td className="px-3 py-2 text-right text-cargo-muted text-xs">{isNumeric && col.median != null ? Number(col.median).toFixed(2) : '—'}</td>
                      <td className="px-3 py-2 text-right text-cargo-muted text-xs">{col.unique_count ?? '—'}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <span className="text-xs text-cargo-muted">{nullPct.toFixed(1)}%</span>
                          <div className="w-12 h-1.5 bg-cargo-border rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${nullBarColor}`} style={{ width: `${Math.min(nullPct, 100)}%` }} />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Profiling rules */}
      {ruleResults.length > 0 && (
        <div>
          <h3 className="text-xs font-medium text-cargo-muted uppercase tracking-wider mb-2">Profiling Rules</h3>
          <div className="grid grid-cols-3 gap-3 mb-3">
            {[
              { label: 'Total', value: ruleResults.length, color: 'text-cargo-text' },
              { label: 'Passed', value: ruleResults.filter((r: any) => r.success).length, color: 'text-green-400' },
              { label: 'Failed', value: ruleResults.filter((r: any) => !r.success).length, color: 'text-red-400' },
            ].map(({ label, value, color }) => (
              <div key={label} className="rounded-lg border border-cargo-border bg-cargo-panel/50 p-2.5 text-center">
                <p className={`text-lg font-bold ${color}`}>{value}</p>
                <p className="text-xs text-cargo-muted">{label}</p>
              </div>
            ))}
          </div>
          <div className="border border-cargo-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-cargo-panel">
                  <th className="px-3 py-2 text-center text-xs font-medium text-cargo-muted w-12">Status</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-cargo-muted">Column</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-cargo-muted">Rule</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-cargo-muted">Description</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-cargo-muted">Observed</th>
                </tr>
              </thead>
              <tbody>
                {ruleResults.map((r: any, i: number) => (
                  <tr key={i} className={`border-t border-cargo-border/50 ${r.success === false ? 'bg-red-500/5' : ''}`}>
                    <td className="px-3 py-2 text-center">
                      {r.success ? (
                        <CheckCircle2 className="w-4 h-4 text-green-400 mx-auto" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-400 mx-auto" />
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-cargo-text text-xs">{r.column}</td>
                    <td className="px-3 py-2 text-cargo-muted text-xs">{(r.rule || '').replace(/_/g, ' ')}</td>
                    <td className="px-3 py-2 text-cargo-muted text-xs">{r.description || '—'}</td>
                    <td className="px-3 py-2 text-cargo-muted text-xs">{r.observed_value != null ? String(r.observed_value) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default ValidationReportModal;
